import { formatarDataHora } from '@/lib/formato';
import { termoDaBusca } from '@/lib/texto';

import type { AdministradorDaPlataforma, AtoDaPlataforma } from './api';

/**
 * A parte da administração da plataforma que não depende de tela (decisão 25,
 * migração 0032): a seção aberta, que vai para a URL; o endereço do convite de
 * plataforma; e a trilha escrita em português de obra, não em nome de função.
 */

export const SECOES = ['visao', 'empresas', 'administradores', 'trilha', 'configuracoes'] as const;
export type Secao = (typeof SECOES)[number];
export const SECAO_PADRAO: Secao = 'visao';

export const ROTULO_DA_SECAO: Readonly<Record<Secao, string>> = {
  visao: 'Visão geral',
  empresas: 'Empresas',
  administradores: 'Administradores',
  trilha: 'Trilha da plataforma',
  /* O que vale para todas as empresas (decisão do dono, 27/09/2026): por
     enquanto, o motor de IA. */
  configuracoes: 'Configurações do sistema',
};

export interface BuscaDaPlataforma {
  readonly secao?: Secao;
  readonly empresa?: string;
  readonly termo?: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function ehSecao(valor: unknown): valor is Secao {
  return typeof valor === 'string' && (SECOES as readonly string[]).includes(valor);
}

/**
 * A seção e a empresa abertas vão para a URL: quem administra salva e manda o
 * endereço de uma empresa. Empresa aberta implica a seção Empresas.
 */
export function validarBuscaDaPlataforma(entrada: Record<string, unknown>): BuscaDaPlataforma {
  const empresa = typeof entrada['empresa'] === 'string' && UUID.test(entrada['empresa']) ? entrada['empresa'].toLowerCase() : null;
  const secao = empresa ? 'empresas' : ehSecao(entrada['secao']) ? entrada['secao'] : SECAO_PADRAO;
  /* Como foi digitado: aparar aqui comia o espaço a cada tecla (`termoDaBusca`); quem apara é `filtrarPorNome`. */
  const termo = termoDaBusca(entrada['termo']);
  return {
    ...(secao !== SECAO_PADRAO ? { secao } : {}),
    ...(empresa ? { empresa } : {}),
    ...(termo && !empresa && secao === 'empresas' ? { termo } : {}),
  };
}

/** O convite de ADMINISTRADOR DA PLATAFORMA abre a página própria, e não a da empresa. */
export function enderecoDoConviteDePlataforma(origem: string, codigo: string): string {
  return `${origem.replace(/\/+$/, '')}/plataforma/primeiro-acesso#${codigo}`;
}

/* As chaves sao os nomes que as migracoes gravam em `infra.trilha_plataforma.acao`
   (0029, 0032, 0033 e 0042), letra por letra: chave que nao bate cai no nome tecnico. */
const ROTULO_DO_ATO: Readonly<Record<string, string>> = {
  entrar: 'Entrada na plataforma',
  emitir_convite_plataforma: 'Convite de plataforma emitido pelo servidor',
  aceitar_convite_plataforma: 'Convite de plataforma aceito',
  criar_empresa: 'Empresa criada',
  reemitir_convite: 'Convite do administrador da empresa reemitido',
  renomear_empresa: 'Empresa renomeada',
  criar_pessoa: 'Pessoa cadastrada',
  ativar_pessoa: 'Pessoa reativada',
  desativar_pessoa: 'Pessoa desativada',
  conceder_papel: 'Papel concedido',
  tirar_papel: 'Papel retirado',
  convite_da_pessoa: 'Convite ou troca de senha emitido',
  encerrar_sessoes: 'Sessões encerradas',
  convidar_administrador: 'Administrador convidado',
  ativar_administrador: 'Administrador reativado',
  desativar_administrador: 'Administrador desativado',
  desativar_empresa: 'Empresa desativada',
  ativar_empresa: 'Empresa reativada',
  abrir_suporte: 'Acesso de suporte aberto',
  configurar_ia: 'IA configurada',
  definir_homologacao: 'Ambiente de homologação ligado ou desligado',
  remover_configuracao_ia: 'Configuração da IA removida',
  testar_ia: 'IA testada',
};

/** O ato em frase curta. Ato que a tela não conhece aparece com o nome técnico, e não some. */
export function rotuloDoAto(acao: string): string {
  return ROTULO_DO_ATO[acao] ?? acao.replaceAll('_', ' ');
}

const texto = (valor: unknown): string | null => (typeof valor === 'string' && valor.trim() !== '' ? valor.trim() : null);

const objeto = (valor: unknown): Readonly<Record<string, unknown>> | null =>
  typeof valor === 'object' && valor !== null && !Array.isArray(valor) ? (valor as Record<string, unknown>) : null;

/**
 * Uma etapa do teste da IA (0042, acao `testar_ia`), como a trilha a guarda:
 * `{ ok, status, ms }`. "falhou (402)" diz de cara que era a conta sem crédito.
 */
function etapaDoTeste(nome: string, valor: unknown): string | null {
  const etapa = objeto(valor);
  if (!etapa || typeof etapa['ok'] !== 'boolean') return null;
  if (etapa['ok']) return `${nome}: funcionou`;
  return typeof etapa['status'] === 'number' ? `${nome}: falhou (${String(etapa['status'])})` : `${nome}: falhou`;
}

/**
 * O complemento do ato, só com o que ajuda a reconhecê-lo: o e-mail, o nome
 * novo, o motivo escrito e quantas sessões caíram. Identificador interno não
 * entra — ninguém reconhece uma pessoa pelo uuid.
 *
 * Nos atos da IA (0042): a configuração nova (`depois`) em protocolo e modelo,
 * e o teste em modelo e resultado de cada etapa. O endereço fica de fora — é
 * longo, e segue inteiro no detalhe da trilha.
 */
export function complementoDoAto(ato: Pick<AtoDaPlataforma, 'detalhe'>): string {
  const d = ato.detalhe;
  const partes: string[] = [];
  const email = texto(d['email']) ?? texto(d['administrador_email']);
  if (email) partes.push(email);
  const nome = texto(d['nome']);
  if (nome) partes.push(`nome: ${nome}`);
  const depois = objeto(d['depois']);
  const protocoloNovo = texto(depois?.['protocolo']);
  if (protocoloNovo) partes.push(`protocolo ${protocoloNovo}`);
  const modeloNovo = texto(depois?.['modelo']);
  if (modeloNovo) partes.push(`modelo ${modeloNovo}`);
  const etapas = [
    etapaDoTeste('resposta', d['resposta']),
    etapaDoTeste('leitura', d['leitura']),
    etapaDoTeste('vetores', d['vetores']),
  ].filter((e): e is string => e !== null);
  if (etapas.length > 0) {
    const testado = texto(d['modelo']);
    if (testado) partes.push(testado);
    partes.push(...etapas);
  }
  const motivo = texto(d['motivo']);
  if (motivo) partes.push(`motivo: ${motivo}`);
  const caidas = typeof d['sessoes_encerradas'] === 'number' ? d['sessoes_encerradas'] : typeof d['encerradas'] === 'number' ? d['encerradas'] : null;
  if (caidas !== null && caidas > 0) partes.push(caidas === 1 ? '1 sessão encerrada' : `${String(caidas)} sessões encerradas`);
  const anulados = typeof d['convites_anulados'] === 'number' ? d['convites_anulados'] : 0;
  if (anulados > 0) partes.push(anulados === 1 ? '1 convite anulado' : `${String(anulados)} convites anulados`);
  return partes.join(' · ');
}

/** Em que pé está o acesso de um administrador da plataforma, sempre com rótulo escrito. */
export function acessoDoAdministrador(
  adm: Pick<AdministradorDaPlataforma, 'ativo' | 'temSenha' | 'conviteAte'>,
  agora: Date,
): { readonly rotulo: string; readonly detalhe: string } {
  const convite = adm.conviteAte ? new Date(adm.conviteAte) : null;
  const aberto = convite !== null && convite.getTime() > agora.getTime();
  if (!adm.ativo) return { rotulo: 'Sem acesso', detalhe: 'Desativado: não entra na administração da plataforma.' };
  if (adm.temSenha) return { rotulo: 'Entra na plataforma', detalhe: aberto ? `Convite novo aberto até ${formatarDataHora(convite)}.` : 'Com senha própria.' };
  if (aberto) return { rotulo: 'Convite aberto', detalhe: `Vale até ${formatarDataHora(convite)}. Ainda não foi aceito.` };
  return { rotulo: 'Sem acesso', detalhe: 'O convite venceu. Convide de novo pelo mesmo e-mail.' };
}

/** Empresas cujo nome contém o termo, sem diferença de acento nem de caixa. */
export function filtrarPorNome<T extends { readonly nome: string }>(itens: readonly T[], termo: string): readonly T[] {
  const limpo = (s: string) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
  const alvo = limpo(termo.trim());
  return alvo ? itens.filter((i) => limpo(i.nome).includes(alvo)) : itens;
}
